import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from '@/lib/api';
import { useEditor, isImageAsset } from "./store";
import { cn } from "@/lib/utils/shadcn";
import {FiSettings, FiInfo} from "react-icons/fi";
import { RiArrowGoBackLine } from "react-icons/ri";
import { Slider } from "@/components/ui/shadcn/slider";
import { Switch } from "@/components/ui/shadcn/switch";
import { LuAudioLines } from "react-icons/lu";
import { Textarea } from "@/components/ui/shadcn/textarea";
import Button from "@/components/ui/Button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/shadcn/dropdown-menu";
import { FiChevronDown, FiDownload } from "react-icons/fi";
import { LuHistory, LuUndo2, LuLock, LuLockOpen, LuMaximize } from "react-icons/lu";
import { RiAiGenerateText } from "react-icons/ri";
import { Button as ShadButton } from '@/components/ui/shadcn/button'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { useModal } from "@/contexts/ModalContext";
import VoiceSettingsModal from "@/components/modals/VoiceSettingsModal";
import { useShow } from "@/contexts/ShowContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/shadcn/select";
import { probeMedia } from "./helpers";
import { nanoid } from "nanoid";
import Loader from "@/components/loader";

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;


// Helper to get file extension from URL
const getFileExtension = (url) => {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const ext = pathname.split('.').pop()?.toLowerCase();
    return ext && ext.length <= 5 ? ext : null;
  } catch {
    const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase();
    return ext && ext.length <= 5 ? ext : null;
  }
}

// Helper to format type/codec info
const getMediaTypeLabel = (asset) => {
  if (!asset) return 'Unknown';
  
  const ext = getFileExtension(asset.url);
  const type = asset.type || asset.mediaType;
  
  if (isImageAsset(asset)) {
    return ext ? `Image (${ext.toUpperCase()})` : 'Image';
  }
  
  if (type === 'video') {
    // Common video codecs by extension
    const codecMap = {
      'mp4': 'H.264/AAC',
      'm4v': 'H.264/AAC',
      'webm': 'VP8/VP9',
      'mov': 'H.264/ProRes',
      'avi': 'Various',
      'mkv': 'Various',
    };
    const codec = ext ? codecMap[ext] : null;
    return codec ? `Video (${codec})` : 'Video';
  }
  
  if (type === 'audio') {
    const codecMap = {
      'mp3': 'MP3',
      'wav': 'PCM',
      'aac': 'AAC',
      'm4a': 'AAC',
      'ogg': 'Vorbis',
      'flac': 'FLAC',
      'webm': 'Opus',
    };
    const codec = ext ? codecMap[ext] : null;
    return codec ? `Audio (${codec})` : 'Audio';
  }
  
  return ext ? ext.toUpperCase() : 'Unknown';
}

// Assumed frame rates by extension (common defaults)
const getEstimatedFps = (asset) => {
  if (!asset || asset.type !== 'video') return null;
  // Most web video is 24-30 fps
  return '30 fps (assumed)';
}

function AudioControls({ clip, clipId, updateClip }) {
    return (
        <div className="flex flex-col gap-4">
            <span className="text-xs text-secondary-foreground">Audio</span>
            <div className="flex flex-col gap-1">
                <label className="text-[12px] text-secondary-foreground/60">Gain</label>
                <div className="flex items-center gap-2 w-full">
                    <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={[clip.gain ?? 1]}
                        onValueChange={value => updateClip(clipId, { gain: value[0] })}
                        className="flex-1"
                    />
                    <div className="text-xs text-secondary-foreground">
                        {(clip.gain ?? 1).toFixed(2)}x
                    </div>
                    <button className="bg-secondary/80 text-secondary-foreground/60 hover:text-secondary-foreground hover:bg-secondary cursor-pointer p-1 rounded-sm" onClick={() => updateClip(clipId, { gain: 1 })}>
                        <RiArrowGoBackLine className="w-3 h-3" />
                    </button>
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <label className="text-[12px] text-secondary-foreground/60">Fade In</label>
                <div className="flex items-center gap-2 w-full">
                    <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={[clip.fadeIn ?? 0]}
                        onValueChange={value => updateClip(clipId, { fadeIn: value[0] })}
                        className="flex-1"
                    />
                    <div className="text-xs text-secondary-foreground">
                        {(clip.fadeIn ?? 0).toFixed(2)}s
                    </div>
                    <button className="bg-secondary/80 text-secondary-foreground/60 hover:text-secondary-foreground hover:bg-secondary cursor-pointer p-1 rounded-sm" onClick={() => updateClip(clipId, { fadeIn: 0 })}>
                        <RiArrowGoBackLine className="w-3 h-3" />
                    </button>
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <label className="text-[12px] text-secondary-foreground/60">Fade Out</label>
                <div className="flex items-center gap-2 w-full">
                    <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={[clip.fadeOut ?? 0]}
                        onValueChange={value => updateClip(clipId, { fadeOut: value[0] })}
                        className="flex-1"
                    />
                    <div className="text-xs text-secondary-foreground">
                        {(clip.fadeOut ?? 0).toFixed(2)}s
                    </div>
                    <button className="bg-secondary/80 text-secondary-foreground/60 hover:text-secondary-foreground hover:bg-secondary cursor-pointer p-1 rounded-sm" onClick={() => updateClip(clipId, { fadeOut: 0 })}>
                        <RiArrowGoBackLine className="w-3 h-3" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function MediaTransformControls({ clip, clipId, asset, updateClip, mediaResolution }) {
    const aspectLocked = clip.mediaAspectLocked !== false;

    const sourceW = mediaResolution?.width ?? CANVAS_WIDTH;
    const sourceH = mediaResolution?.height ?? CANVAS_HEIGHT;
    const sourceAspect = sourceW / sourceH;

    const currentW = clip.mediaWidth ?? sourceW;
    const currentH = clip.mediaHeight ?? sourceH;
    const currentX = clip.mediaPosX ?? 0;
    const currentY = clip.mediaPosY ?? 0;

    const handleWidthChange = (raw) => {
        const w = Math.max(1, Math.round(Number(raw) || 0));
        if (aspectLocked) {
            const h = Math.max(1, Math.round(w / sourceAspect));
            updateClip(clipId, { mediaWidth: w, mediaHeight: h });
        } else {
            updateClip(clipId, { mediaWidth: w });
        }
    };

    const handleHeightChange = (raw) => {
        const h = Math.max(1, Math.round(Number(raw) || 0));
        if (aspectLocked) {
            const w = Math.max(1, Math.round(h * sourceAspect));
            updateClip(clipId, { mediaWidth: w, mediaHeight: h });
        } else {
            updateClip(clipId, { mediaHeight: h });
        }
    };

    const handlePosXChange = (raw) => {
        updateClip(clipId, { mediaPosX: Math.round(Number(raw) || 0) });
    };

    const handlePosYChange = (raw) => {
        updateClip(clipId, { mediaPosY: Math.round(Number(raw) || 0) });
    };

    const handleFit = () => {
        const scale = Math.max(CANVAS_WIDTH / sourceW, CANVAS_HEIGHT / sourceH);
        const w = Math.round(sourceW * scale);
        const h = Math.round(sourceH * scale);
        updateClip(clipId, { mediaWidth: w, mediaHeight: h, mediaPosX: 0, mediaPosY: 0 });
    };

    const handleReset = () => {
        updateClip(clipId, {
            mediaWidth: sourceW,
            mediaHeight: sourceH,
            mediaPosX: 0,
            mediaPosY: 0,
            mediaAspectLocked: true,
        });
    };

    const isDefault =
        (clip.mediaWidth == null || clip.mediaWidth === sourceW) &&
        (clip.mediaHeight == null || clip.mediaHeight === sourceH) &&
        (clip.mediaPosX == null || clip.mediaPosX === 0) &&
        (clip.mediaPosY == null || clip.mediaPosY === 0);

    return (
        <div className="flex flex-col gap-4">
            <span className="text-xs text-secondary-foreground">Transform</span>
            <div className="flex gap-2">
                <div className="flex flex-col gap-4 flex-1">
                    <div className="flex flex-col gap-1">
                        <label className="text-[12px] text-secondary-foreground/60">Position X</label>
                        <input
                            type="number"
                            className="w-full bg-secondary text-secondary-foreground text-xs rounded-sm px-2 py-1 outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={currentX}
                            onChange={(e) => handlePosXChange(e.target.value)}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[12px] text-secondary-foreground/60">Position Y</label>
                        <input
                            type="number"
                            className="w-full bg-secondary text-secondary-foreground text-xs rounded-sm px-2 py-1 outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={currentY}
                            onChange={(e) => handlePosYChange(e.target.value)}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-1">
                    <div className="flex flex-col flex-1 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-[12px] text-secondary-foreground/60">Width</label>
                            <input
                                type="number"
                                className="w-full bg-secondary text-secondary-foreground text-xs rounded-sm px-2 py-1 outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={currentW}
                                onChange={(e) => handleWidthChange(e.target.value)}
                                min={1}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[12px] text-secondary-foreground/60">Height</label>
                            <input
                                type="number"
                                className="w-full bg-secondary text-secondary-foreground text-xs rounded-sm px-2 py-1 outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={currentH}
                                onChange={(e) => handleHeightChange(e.target.value)}
                                min={1}
                            />
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-1 pt-5 items-center justify-center h-full">
                        <button
                            className={cn(
                                "p-1.5 rounded-sm cursor-pointer",
                                aspectLocked
                                    ? "bg-primary/20 text-primary hover:bg-primary/30"
                                    : "bg-secondary/80 text-secondary-foreground/40 hover:text-secondary-foreground hover:bg-secondary"
                            )}
                            onClick={() => updateClip(clipId, { mediaAspectLocked: !aspectLocked })}
                            title={aspectLocked ? "Unlock aspect ratio" : "Lock aspect ratio"}
                        >
                            {aspectLocked ? <LuLock className="w-3 h-3" /> : <LuLockOpen className="w-3 h-3" />}
                        </button>
                        <button
                            className="p-1.5 rounded-sm cursor-pointer bg-secondary/80 text-secondary-foreground/40 hover:text-secondary-foreground hover:bg-secondary"
                            onClick={handleFit}
                            title="Fill canvas"
                        >
                            <LuMaximize className="w-3 h-3" />
                        </button>
                        <button
                            className="p-1.5 rounded-sm cursor-pointer bg-secondary/80 text-secondary-foreground/40 hover:text-secondary-foreground hover:bg-secondary"
                            onClick={handleReset}
                            disabled={isDefault}
                            title="Reset to original size"
                        >
                            <RiArrowGoBackLine className="w-3 h-3" />
                        </button>
                    </div>
                    
                </div>
            </div>
        </div>
    );
}


function getClipSettingsType(clip, asset, track) {
    if (!clip) return null;
    if (asset && (isImageAsset(asset) || asset.type === 'video') && track?.type === 'video') return 'visual';
    if ((asset && !isImageAsset(asset)) || (!asset && track && (track.type === 'audio' || track.type === 'video'))) return 'audio';
    return 'none';
}

export default function TimelineElementEditor() {
    const selectedClipIds = useEditor((s) => s.selectedClipIds);
    const clips = useEditor((s) => s.clips);
    const assets = useEditor((s) => s.assets);
    const tracks = useEditor((s) => s.tracks);
    const [view, setView] = useState('settings');
    const updateClipSingle = useEditor((s) => s.updateClip);
    const addAsset = useEditor((s) => s.addAsset);
    const removeAsset = useEditor((s) => s.removeAsset);
    const updateScriptAsset = useEditor((s) => s.updateScriptAsset);
    const addClip = useEditor((s) => s.addClip);
    const removeClip = useEditor((s) => s.removeClip);
    const [speech, setSpeech] = useState('');
    const [characterId, setCharacterId] = useState(null);
    const [scriptId, setScriptId] = useState(null);
    const [characters, setCharacters] = useState([]);
    const {openModal} = useModal()
    const {runTaskAndPoll, taskStatus, taskState} = useKagamiBridge()
    const {show} = useShow()
    const episodeId = useEditor((s) => s.episodeId);
    const segmentId = useEditor((s) => s.segmentId);
    const [updating, setUpdating] = useState(false);
    const [taskStatusText, setTaskStatusText] = useState('');
    const [mediaResolution, setMediaResolution] = useState(null);

    const isMultiSelect = selectedClipIds.length > 1;

    const allSameType = useMemo(() => {
        if (selectedClipIds.length <= 1) return true;
        const types = selectedClipIds.map((id) => {
            const c = clips.find((cl) => cl.id === id);
            if (!c) return null;
            const a = c.assetId ? assets[c.assetId] : null;
            const t = tracks.find((tr) => tr.id === c.trackId);
            return getClipSettingsType(c, a, t);
        });
        return types.every((t) => t !== null && t === types[0]);
    }, [selectedClipIds, clips, assets, tracks]);

    const clipId = selectedClipIds.length >= 1 ? selectedClipIds[selectedClipIds.length - 1] : null;
    const clip = clipId ? clips.find((c) => c.id === clipId) : null;
    const asset = clip ? assets[clip.assetId] : null;

    const updateClip = useCallback((targetClipId, updates, options) => {
        if (isMultiSelect && allSameType) {
            selectedClipIds.forEach((id) => updateClipSingle(id, updates, options));
        } else {
            updateClipSingle(targetClipId, updates, options);
        }
    }, [isMultiSelect, allSameType, selectedClipIds, updateClipSingle]);

    useEffect(() => {
        setTaskStatusText(taskStatus)
    }, [taskStatus, taskState])

    useEffect(() => {
        setMediaResolution(null);
        if (!asset?.url) return;
        const isVisual = isImageAsset(asset) || asset.type === 'video';
        if (!isVisual) return;
        if (asset.width && asset.height) {
            setMediaResolution({ width: asset.width, height: asset.height });
            return;
        }
        let cancelled = false;
        if (isImageAsset(asset)) {
            const img = new Image();
            img.onload = () => { if (!cancelled) setMediaResolution({ width: img.naturalWidth, height: img.naturalHeight }); };
            img.src = asset.url;
        } else {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => { if (!cancelled) setMediaResolution({ width: video.videoWidth, height: video.videoHeight }); };
            video.src = asset.url;
        }
        return () => { cancelled = true; };
    }, [asset?.url, asset?.type, asset?.width, asset?.height]);

    useEffect(() => {
        setSpeech(asset?.speech || '')
        setCharacterId(asset?.characterId || null)
        setScriptId(asset?.scriptId || null)
        if(asset){
            //loadCharacters()
        }
    }, [clipId, asset?.speech, asset?.characterId]);
    
    if (selectedClipIds.length === 0) {
        return null;
    }
    
    if (isMultiSelect && !allSameType) {
        return null;
    }
    
    if (!clip) {
        return null;
    }

    const track = tracks.find((t) => t.id === clip.trackId);


    const handleEditScript = async(prompt) => {
        try{
            setUpdating(true)
            await runTaskAndPoll('edit-script-grok', {
                scriptId: asset?.scriptId, 
                prompt: prompt
            })

            setTaskStatusText('Updating script...')
            const scriptResponse = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${asset?.sceneId}/script/${asset?.scriptId}`)
            if(!scriptResponse.ok){
                console.error('Failed to fetch script');
                return;
            }
            const script = await scriptResponse.json()
            setSpeech(script.speech)
            if (clip?.assetId && asset?.scriptId) {
                updateScriptAsset(clip.assetId, {
                    speech: script.speech,
                    name: script.speech,
                });
            }
        }catch(error){
            console.log(error)
        }
        finally{
            setUpdating(false)
            setTaskStatusText('')
        }
    }
    
    const generateVoice = async (advanced) => {
        const character = show.characters.find(ch=>ch._id === characterId)
        if(!character)return
        const voiceId = character.voice_ids.find(v=>v.language==='en')?.voice_id
        if(!voiceId)return

        let voiceSettings = character.voice_settings

        if(advanced){
            const scriptResponse = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${asset?.sceneId}/script/${asset?.scriptId}`)
            if(!scriptResponse.ok){
                console.error('Failed to fetch script');
                return;
            }
            const script = await scriptResponse.json()
            voiceSettings = script.voice_settings || voiceSettings
            const newSettings = await openModal(<VoiceSettingsModal voiceSettings={voiceSettings} />)
            if(!newSettings)return
            voiceSettings = newSettings
        }

        try{
            setUpdating(true)
            if(asset?.scriptId){
                const scriptResponse = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segmentId}/scenes/${asset?.sceneId}/script/${asset?.scriptId}`,{
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        speech,
                        character: characterId
                    })
                })

                if(!scriptResponse.ok){
                    console.error('Failed to update script');
                    return;
                }
            }

            const response = await runTaskAndPoll({
                function: 'generate-voice',
                showId: show?._id,
                payload: {
                    ...(asset?.scriptId ? {scriptId: asset?.scriptId} : {}),
                    ...(!asset?.scriptId && {
                        prompt: speech,
                        voiceId: show.characters.find(ch=>ch._id === characterId)?.voice_ids.find(v=>v.language==='en')?.voice_id
                    }),
                    ...(voiceSettings ? {voiceSettings} : {})
                }
            })

            const playload = response?.payload
            if (!playload?.voiceUrl) {
                console.warn('No voiceUrl in response');
                return;
            }

            const meta = await probeMedia(playload.voiceUrl, 'audio')
            const voiceUrl = playload.voiceUrl

            if (clip.assetId && asset?.scriptId) {
                updateScriptAsset(clip.assetId, {
                    url: voiceUrl,
                    duration: meta.duration,
                    name: speech,
                    characterId: characterId,
                    speech: speech,
                });
            } else {
                const oldAssetId = clip.assetId;
                const oldClipStart = clip.start;
                const oldClipTrackId = clip.trackId;

                const newAssetId = nanoid();
                addAsset({
                    id: newAssetId,
                    url: voiceUrl,
                    type: 'audio',
                    duration: meta.duration,
                    name: speech,
                    mediaType: 'audio',
                    scriptId: asset?.scriptId || null,
                    sceneId: asset?.sceneId || null,
                    characterId: characterId,
                    speech: speech,
                });

                addClip({
                    assetId: newAssetId,
                    trackId: oldClipTrackId,
                    start: oldClipStart,
                    allowTrimming: true,
                    allowSplit: true,
                });

                removeClip(clipId);

                if (oldAssetId) {
                    const stateAfterClipRemoval = useEditor.getState();
                    const isAssetUsedElsewhere = stateAfterClipRemoval.clips.some(
                        (c) => c.assetId === oldAssetId
                    );
                    if (!isAssetUsedElsewhere && stateAfterClipRemoval.assets[oldAssetId]) {
                        removeAsset(oldAssetId);
                    }
                }
            }
        }catch(error){
            console.log(error)
        }finally{
            setUpdating(false)
            setTaskStatusText('')
        }
    }

  return (
    <div className="w-full h-full bg-background flex flex-col overflow-hidden">
        <div className='flex items-center justify-between p-2'>
            <h3 className="text-sm font-semibold text-foreground truncate flex-1">
                {isMultiSelect
                    ? `${selectedClipIds.length} Clips Selected`
                    : (asset && (asset.name || asset.url?.split("/").pop()) 
                        ? (asset.name || asset.url?.split("/").pop())
                        : "Selected Clip")}
            </h3>
            {!isMultiSelect && <div className='flex overflow-hidden rounded-md'>
                <button className={`cursor-pointer text-xs flex items-center p-2 ${view === 'settings' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`} onClick={() => setView('settings')}>
                    <FiSettings className='w-3 h-3' />
                </button>
                <button className={`cursor-pointer text-xs flex items-center p-2 ${view === 'info' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'}`} onClick={() => setView('info')}>
                    <FiInfo className='w-3 h-3' />
                </button>
            </div>}
        </div>
    
        {!isMultiSelect && view === 'info' && <div className="flex flex-col gap-0 px-2 flex-1 overflow-y-auto text-xs text-secondary-foreground font-mono">        
            
            {/* Type & Format */}
            {asset && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                    <span>Type:</span>
                    <span className="text-secondary-foreground">{getMediaTypeLabel(asset)}</span>
                </div>
            )}
            
            {/* Track */}
            {track && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                    <span>Track:</span>
                    <span className="text-secondary-foreground">{track.name || clip.trackId}</span>
                </div>
            )}
            
            {/* Resolution (for video/image) */}
            {asset && (asset.type === 'video' || isImageAsset(asset)) && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                    <span>Resolution:</span>
                    <span className="text-secondary-foreground">
                        {mediaResolution ? `${mediaResolution.width}×${mediaResolution.height}` : '…'}
                    </span>
                </div>
            )}
            
            {/* Frame rate (for video) */}
            {asset?.type === 'video' && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                    <span>Frame Rate:</span>
                    <span className="text-secondary-foreground">{getEstimatedFps(asset)}</span>
                </div>
            )}
            
            {/* Timeline position */}
            <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1 border-t border-zinc-700/50 mt-1 pt-2">
                <span>Timeline:</span>
                <span className="text-secondary-foreground">
                    {clip.start.toFixed(2)}s → {(clip.start + clip.duration).toFixed(2)}s
                </span>
            </div>
            
            {/* Asset time (in point / total duration) */}
            {asset && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                    <span>Asset time:</span>
                    <span className="text-secondary-foreground">
                        {clip.in.toFixed(3)}s / {asset.duration === Infinity ? '∞' : asset.duration?.toFixed(2) ?? '∞'}s
                    </span>
                </div>
            )}
            
            {/* Clip trimming info */}
            <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1">
                <span>In/Out:</span>
                <span className="text-secondary-foreground">
                    {clip.in.toFixed(2)}s → {(clip.in + clip.duration).toFixed(2)}s
                </span>
            </div>
            
            {/* Gain (for audio and empty clips) */}
            {(asset?.type === "audio" || (!asset && track && (track.type === "audio" || track.type === "video"))) && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1 border-t border-zinc-700/50 mt-1 pt-2">
                    <span>Gain:</span>
                    <span className="text-secondary-foreground">{(clip.gain ?? 1).toFixed(2)}x</span>
                </div>
            )}

            {(asset?.type === "audio" && asset?.scriptId) && (
                <div className="flex justify-between gap-2 text-secondary-foreground/60 py-1 border-t border-zinc-700/50 mt-1 pt-2">
                    <span>Imported from a script</span>
                </div>
            )}
        </div>}
        {(view === 'settings' || isMultiSelect) && <div className="flex gap-6 flex-col p-2 pr-4 flex-1 overflow-y-auto">
            {asset && (isImageAsset(asset) || asset.type === 'video') && track?.type === 'video' && (
                <MediaTransformControls clip={clip} clipId={clipId} asset={asset} updateClip={updateClip} mediaResolution={mediaResolution} />
            )}

            {((asset && !isImageAsset(asset)) || (!asset && track && (track.type === "audio" || track.type === "video"))) && (
                <AudioControls clip={clip} clipId={clipId} updateClip={updateClip} />
            )}
        </div>}
    </div>
  );
}
